import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { colors } from '../../global';
import { useRouter, useStoreData } from '../../hooks';
import { CoreContext } from '../../providers';
import { media } from '../../services/media';
import { themeStyles } from '../../services/themeStyles';
import { VisibleOn } from '../VisibleOn';
import { Container } from './Container';
import { HeaderMenu } from './HeaderMenu';
import { Logo } from './Logo';
import { MenuTrigger } from './MenuTrigger';
import { MobileMenuClosedButton } from './MobileMenuCloseButton';

interface MainHeaderProps {
    setHeight: React.Dispatch<React.SetStateAction<number>>;
}

export const MainHeader = ({ setHeight }: MainHeaderProps) => {
    const { settings } = useContext(CoreContext);
    const { pathname } = useRouter();
    const ref = useRef<HTMLHeadElement>(null);

    useEffect(() => {
        setHeight(ref.current?.clientHeight || 0);
    }, [ref]);

    const [menuOpened, setMenuOpenState] = useState(false);

    const headerData = useStoreData().storyblok.HeaderItem;

    const handleMenuClick = useCallback(() => {
        setMenuOpenState(!menuOpened);
    }, [menuOpened, setMenuOpenState]);

    const handleHeaderMenuClick = useCallback(() => {
        setMenuOpenState(false);
    }, [setMenuOpenState]);

    useEffect(() => {
        handleHeaderMenuClick();
    }, [pathname]);

    if (!headerData) {
        return null;
    }

    return (
        <>
            <Header ref={ref}>
                <Container>
                    <Logo src={headerData.content.logo} />
                    {headerData.content.navigationMenu.length ? (
                        <>
                            <VisibleOn mobile tablet>
                                {!menuOpened && (
                                    <MenuTrigger onClick={handleMenuClick} />
                                )}
                                {menuOpened && (
                                    <>
                                        <MobileMenuClosedButton
                                            type="button"
                                            onClick={handleHeaderMenuClick}
                                        >
                                            X
                                        </MobileMenuClosedButton>
                                        <HeaderMenu
                                            mobile
                                            onCloseClick={handleHeaderMenuClick}
                                            isMenuOpened={menuOpened}
                                            items={
                                                headerData.content
                                                    .navigationMenu
                                            }
                                        />
                                    </>
                                )}
                            </VisibleOn>
                            <VisibleOn desktop>
                                <HeaderMenu
                                    onCloseClick={handleHeaderMenuClick}
                                    items={headerData.content.navigationMenu}
                                />
                            </VisibleOn>
                        </>
                    ) : null}
                </Container>
            </Header>
        </>
    );
};

const HeaderWhiteBackground = css`
    background-color: rgba(255, 255, 255, 0.8);
`;

const Upscore = themeStyles(
    'upscore',
    css`
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: rgba(255, 255, 255, 0.8);

        ${media.max('md')} {
            padding: 0 15px;
        }

        ${media.between('md', 'lg')} {
            padding: 0 25px;
        }

        ${media.max('lg')} {
            color: ${colors.white};
        }

        ${media.min('lg')} {
            color: ${colors.turquoiseBlue};
        }

        ${media.between('lg', 'xxl')} {
            padding: 0 50px;
        }

        ${media.min('xxl')} {
            padding: 0 80px;
        }
    `,
);

const Pengestyrke = themeStyles(
    'pengestyrke',
    css`
        background-color: ${colors.elephant};
        color: ${colors.white};
    `,
);

const Formue = themeStyles(
    'formue',
    css`
        background-color: ${colors.pampas};
        color: ${colors.stratos};
    `,
);

const Header = styled.header`
    position: relative;

    ${Upscore}
    ${Pengestyrke}

    ${Formue}
`;
